/**
 * ProfileStats — TikTok-style 3-stat row.
 * Web port of client-native/src/features/profile/components/ProfileStats.tsx.
 *
 * listening / words / streak, each a vertical stack (24px raster icon, value
 * xlarge bold primary, label small secondary), separated by thin 1px
 * greyLightest vertical dividers (height 32, marginHorizontal 8).
 */
import { FC } from "react";
import { colors } from "@/ui";
import { timerIcon, fireIcon, openBookIcon } from "./profileAssets";
import { formatStatRowListening, formatStatNumber } from "./profileFormat";

export type StatType = "listening" | "words" | "streak";

interface ProfileStatsProps {
  listeningTimeSeconds: number;
  wordsKnown: number;
  streak: number;
  onStatPress?: (statType: StatType) => void;
}

const Divider: FC = () => (
  <div
    style={{
      width: 1,
      height: 32,
      backgroundColor: colors.greyLightest,
      margin: "0 8px",
      flexShrink: 0,
    }}
  />
);

const StatItem: FC<{
  icon: string;
  value: string;
  label: string;
  onPress?: () => void;
}> = ({ icon, value, label, onPress }) => (
  <button
    type="button"
    onClick={onPress}
    style={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      padding: "8px 0",
      background: "none",
      border: "none",
      cursor: "pointer",
    }}
  >
    <img
      src={icon}
      alt=""
      style={{ width: 24, height: 24, marginBottom: 4, objectFit: "contain" }}
    />
    <span
      className="text-xl font-bold"
      style={{
        color: colors.primary,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
    <span className="text-sm" style={{ color: colors.secondary }}>
      {label}
    </span>
  </button>
);

const ProfileStats: FC<ProfileStatsProps> = ({
  listeningTimeSeconds,
  wordsKnown,
  streak,
  onStatPress,
}) => {
  const listening = formatStatRowListening(listeningTimeSeconds);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px 24px",
      }}
    >
      <StatItem
        icon={timerIcon}
        value={listening.value}
        label={listening.label}
        onPress={() => onStatPress?.("listening")}
      />
      <Divider />
      <StatItem
        icon={openBookIcon}
        value={formatStatNumber(wordsKnown)}
        label="Words"
        onPress={() => onStatPress?.("words")}
      />
      <Divider />
      <StatItem
        icon={fireIcon}
        value={streak.toString()}
        label="Streak"
        onPress={() => onStatPress?.("streak")}
      />
    </div>
  );
};

export default ProfileStats;
